import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';

import { distanceEquals, Line2D, Point2D } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getPickingObjectInfo } from '../../../../../../store/selectors/pickingSelectors';
import { useAppSelector } from '../../../../../hooks';
import { BearingEnvelopePropertiesProps } from './BearingEnvelopeProperties';

function LengthInput(props: { value: number; disabled?: boolean; onChange: (value: number) => void }) {
  return (
    <DistanceField
      label="Desired Length"
      value={props.value}
      disabled={props.disabled}
      fullWidth={false}
      onSubmit={props.onChange}
    />
  );
}

export function BearingEnvelopeLengthTool(props: BearingEnvelopePropertiesProps) {
  const leftPoint = props.properties.leftPoint ? Point2D.fromObject(props.properties.leftPoint) : null;
  const rightPoint = props.properties.rightPoint ? Point2D.fromObject(props.properties.rightPoint) : null;

  const isPicking = useAppSelector((state) => {
    const info = getPickingObjectInfo(state);
    return info?.type === 'pickingLayoutPoint' && info.target.startsWith('bearing-envelope');
  });

  const disabled = leftPoint == null || rightPoint == null || isPicking || props.disabled;
  const length = leftPoint && rightPoint ? leftPoint.distanceTo(rightPoint) : 0;

  const [desiredLength, setDesiredLength] = useState<number | null>(null);

  function onApplyLength(endToMutate: 'left' | 'right') {
    if (desiredLength == null || distanceEquals(length, desiredLength)) {
      return;
    }

    if (leftPoint == null || rightPoint == null) {
      throw new Error('BearingEnvelopeLengthTool submitted with null points. This is a bug.');
    }

    const basePoint = endToMutate === 'left' ? rightPoint : leftPoint;
    const endPoint = endToMutate === 'left' ? leftPoint : rightPoint;

    const newEndPoint = Line2D.fromPoints(basePoint, endPoint).getPointAlongLine(desiredLength);

    switch (endToMutate) {
      case 'left':
        props.onChange({ leftPoint: newEndPoint.toObject() });
        break;

      case 'right':
        props.onChange({ rightPoint: newEndPoint.toObject() });
        break;

      default:
        assertExhaustiveSwitch(endToMutate);
    }
  }

  return (
    <>
      <Typography
        variant="body2"
        sx={{
          color: disabled ? 'text.disabled' : undefined,
        }}
      >
        {disabled
          ? 'Edit bearing (available once both points have been chosen):'
          : 'Edit bearing by moving start or end point:'}
      </Typography>

      <Stack direction="row" spacing={1}>
        <LengthInput
          key={length}
          value={length}
          disabled={disabled}
          onChange={(length) => void setDesiredLength(length)}
        />

        <Button
          variant="outlined"
          disabled={disabled}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => void onApplyLength('left')}
        >
          Edit Start
        </Button>
        <Button
          variant="outlined"
          disabled={disabled}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => void onApplyLength('right')}
        >
          Edit End
        </Button>
      </Stack>
    </>
  );
}
